/**
 * `--tbl-offset` for the one table with a height of its own on a Strategic KPIs tab (kit contract, design.md §2 rule 5):
 * the table runs to the viewport's lower edge, measured as if the page scroller stood at the top, so the number does not
 * move while the reader scrolls. Three shapes, one per way the tabs hold their table; each page used to carry its own copy.
 */
import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

/** The gap below the table to the viewport edge (the scroller's p-6), as in SidePanel. */
export const BOTTOM_GUTTER = 24;

/** The scroll parent of an element (the Layout's <main>). */
export function scrollParent(el: HTMLElement | null): HTMLElement | null {
    for (let p = el?.parentElement ?? null; p; p = p.parentElement) {
        const oy = getComputedStyle(p).overflowY;
        if (oy === 'auto' || oy === 'scroll') return p;
    }
    return null;
}

/**
 * The top of the `.tbl-wrap` inside a body element (the body may hold either of two views), plus the gutter. Re-measured
 * when `deps` change. Returns the ref for the body and the style that sets the variable.
 */
export function useBodyTableOffset(deps: unknown[]) {
    const body = useRef<HTMLDivElement>(null);
    const [offset, setOffset] = useState<number | null>(null);
    useLayoutEffect(() => {
        const el = body.current;
        if (!el) return;
        const scroller = scrollParent(el);
        const measure = () => {
            const wrap = el.querySelector<HTMLElement>('.tbl-wrap');
            if (!wrap) { setOffset(null); return; }
            const top = wrap.getBoundingClientRect().top + (scroller?.scrollTop ?? window.scrollY);
            setOffset(Math.max(0, Math.round(top + BOTTOM_GUTTER)));
        };
        measure();
        window.addEventListener('resize', measure);
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        ro?.observe(el);
        if (scroller) ro?.observe(scroller);
        return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return { body, style: offset !== null ? ({ '--tbl-offset': `${offset}px` } as CSSProperties) : undefined };
}

/**
 * A card whose table box is its last block: the scroller's top edge, plus everything of the card that is not the table
 * box, plus the gutter. Re-measured when `deps` change.
 */
export function useCardTableOffset(card: RefObject<HTMLElement>, wrap: RefObject<HTMLElement>, deps: unknown[]): number | null {
    const [px, setPx] = useState<number | null>(null);
    useLayoutEffect(() => {
        const c = card.current;
        const w = wrap.current;
        if (!c || !w) { setPx(null); return; }
        const scroller = scrollParent(c);
        const measure = () => {
            const top = scroller ? Math.max(0, scroller.getBoundingClientRect().top) : 0;
            setPx(Math.round(top + (c.offsetHeight - w.offsetHeight) + BOTTOM_GUTTER));
        };
        measure();
        window.addEventListener('resize', measure);
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (ro) { ro.observe(c); if (scroller) ro.observe(scroller); }
        return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return px;
}

/** The top of a wrapper, plus the gutter; re-measured when the view above it (`root`) changes size. Only while `active`. */
export function useWrapTableOffset(root: RefObject<HTMLElement>, wrap: RefObject<HTMLElement>, active: boolean): number | null {
    const [offset, setOffset] = useState<number | null>(null);
    useLayoutEffect(() => {
        const w = wrap.current;
        if (!active || !w) return;
        const scroller = scrollParent(w);
        const measure = () => {
            const top = w.getBoundingClientRect().top + (scroller ? scroller.scrollTop : window.scrollY);
            setOffset(Math.round(top + BOTTOM_GUTTER));
        };
        measure();
        window.addEventListener('resize', measure);
        const ro = typeof ResizeObserver !== 'undefined' && root.current ? new ResizeObserver(measure) : null;
        if (ro && root.current) ro.observe(root.current);
        return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
    }, [active, root, wrap]);
    return offset;
}
